/* eslint-disable no-param-reassign */
/* eslint-disable query-config/enforce-query-config */
import React, { useState, useMemo } from 'react';
import { useFind, usePopulate, useFindRecord } from 'src/api/APIHooks';
import { dates } from '@unite-us/app-components';
import { get, isEmpty, upperFirst, isEqual, replace, startCase } from 'lodash';
import moment from 'moment';

const MONTHS_ENUM = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

// can be added to a common date utility file
// if needed for referrals/cases
const getNoteDates = (interactionDate) => {
  const date = interactionDate.split('T')[0];
  if (!date) {
    return {
      day: '',
      month: '',
      year: '',
      fullDate: '',
    };
  }

  const dateObj = new Date(interactionDate);
  const year = date.substr(0, 4);
  const day = date.substr(8, 2);
  const monthIndex = date.substr(5, 2);
  const month = MONTHS_ENUM[parseInt(monthIndex, 10) - 1];

  return {
    day,
    month,
    year,
    time: moment(dateObj).format('h:mm a'),
    fullDate: date,
  };
};

const processNoteForDisplay = (note) => {
  let noteType;
  let added = '';
  const stateChange = get(note, 'details.context', null);
  const updateChange = get(note, 'details.type', null) === 'update';

  if (isEqual(note.category, 'interaction')) {
    noteType = 'Interaction ';
  } else if (isEqual(note.category, 'service_provided')) {
    noteType = 'Service Provided ';
  } else if (stateChange) {
    noteType = startCase(replace(stateChange, /_/g, ' '));
  } else if (updateChange) {
    noteType = 'Updated ';
  } else {
    noteType = 'Note ';
    added = 'added';
  }

  const date = dates.formatLocalDateTime(get(note, 'created_at'));
  const interactionDate = get(note, 'created_at');

  return {
    ...note,
    _processedNoteType: noteType,
    _processedAdded: added,
    _processedDate: date,
    interactionDate: getNoteDates(interactionDate),
    interactionType: upperFirst(get(note, 'details.interaction.type', '')),
    duration: get(note, 'details.interaction.duration', ''),
  };
};

const initialPagingState = { size: 10, page: 1 };
const placeholderMessageResponse = {
  data: {
    data: [],
    paging: {
      number: 1,
      size: 10,
      total_count: 10,
      total_pages: 1,
    },
  },
};

const VALID_NOTES_SUBJECT_TYPES = ['person', 'case', 'referral'];
const DEFAULT_SUBJECT_NAME = 'Case';

export const normalizeNotesSubjectType = (subjectType) => {
  if (typeof subjectType !== 'string') {
    return null;
  }

  const normalizedSubjectType = subjectType.trim().toLowerCase();

  return VALID_NOTES_SUBJECT_TYPES.includes(normalizedSubjectType) ?
    normalizedSubjectType :
    null;
};

const getNotesQueryEnabled = ({ subject, subjectType, providerId }) => (
  Boolean(subject && subjectType && providerId)
);

const getSubjectQueryEnabled = ({ subject, subjectType }) => (
  Boolean(subject && subjectType)
);

const getSubjectName = (subjectType) => ({
  case: 'Case',
  referral: 'Referral',
  person: 'Case',
}[subjectType] || DEFAULT_SUBJECT_NAME);

const mergeCaseData = ({ caseData, caseRecord }) => {
  const hydratedCase = get(caseRecord, 'data.data');

  if (!hydratedCase) {
    return caseData;
  }

  return {
    ...caseData,
    case: hydratedCase,
  };
};

const getDisplayNotes = ({
  notesData,
  isPopulateLoading,
  serviceName,
  subjectName,
}) => {
  if (isEmpty(notesData) || isPopulateLoading) {
    return [];
  }

  return notesData.map((note) => processNoteForDisplay(note, serviceName, subjectName));
};

export const useMessages = ({ subject, providerId }) => {
  const [page, setPage] = useState(initialPagingState.page);
  const [pageSize, setPageSize] = useState(initialPagingState.size);
  const {
    data: messagesResponse,
    isFetching: isLoading,
  } = useFind('communication', {
    person: subject,
    'sender.provider': providerId,
  }, {
    page: {
      number: page,
      size: pageSize,
    },
    queryConfig: {
      placeholderData: placeholderMessageResponse,
      enabled: Boolean(subject),
    },
  });

  const messages = get(messagesResponse, 'data.data', []);
  usePopulate('person', 'person', messages);
  usePopulate('sender', 'employee', messages);

  messages.forEach((m) => {
    if (!m.details) {
      m.details = {};
    }

    const messageBody = get(m, 'details.message_body', '');
    const fallbackMessage = get(m, 'details.message', '');

    m.details.displayMessage = m.medium === 'email' ?
      messageBody || fallbackMessage :
      messageBody;

    const interactionDate = get(m, 'created_at');
    if (!interactionDate) { return; }
    m.interactionDate = getNoteDates(interactionDate);
    m.interactionType = '';
    m.Title = () => (
      <p>{ m.details.message_subject }</p>
    );
  });

  return {
    messages,
    response: messagesResponse,
    isLoading,
    page,
    pageSize,
    setPageSize: (val) => setPageSize(val),
    setPageNumber: (val) => setPage(val),
  };
};

export const useNotes = ({ subject, subjectType, providerId }) => {
  const [page, setPage] = useState(initialPagingState.page);
  const [pageSize, setPageSize] = useState(initialPagingState.size);
  const normalizedSubjectType = normalizeNotesSubjectType(subjectType);
  const isNotesQueryEnabled = getNotesQueryEnabled({
    subject,
    subjectType: normalizedSubjectType,
    providerId,
  });
  const isSubjectQueryEnabled = getSubjectQueryEnabled({
    subject,
    subjectType: normalizedSubjectType,
  });
  const { data: notesResponse, isFetching: isLoading } = useFind('notes', {
    'subject.type': normalizedSubjectType,
    subject,
    category: 'general,interaction,service_provided',
  }, {
    page: {
      number: page,
      size: pageSize,
    },
    context: {
      provider: providerId,
    },
    queryConfig: {
      enabled: isNotesQueryEnabled,
    },
  });

  const { data: subjectResponse } = useFindRecord(
    normalizedSubjectType,
    subject,
    { queryConfig: { enabled: isSubjectQueryEnabled } },
  );

  const caseData = useMemo(() => get(subjectResponse, 'data.data'), [subjectResponse]);
  const caseId = useMemo(() => get(caseData, 'case.id', null), [caseData]);

  const notesData = useMemo(() => get(notesResponse, 'data.data', []), [notesResponse]);

  const employeeQuery = usePopulate('employee', 'employee', notesData);
  const providerQuery = usePopulate('employee.provider', 'provider', notesData);

  const isPopulateLoading = employeeQuery?.isFetching || providerQuery?.isFetching;

  const { data: caseRecord } = useFindRecord(
    'case',
    caseId,
    {
      queryConfig: {
        enabled: Boolean(normalizedSubjectType === 'referral' && caseId),
      },
    },
  );

  const kase = useMemo(() => mergeCaseData({ caseData, caseRecord }), [caseData, caseRecord]);

  const subjectName = useMemo(
    () => getSubjectName(normalizedSubjectType),
    [normalizedSubjectType],
  );

  const serviceName = useMemo(() => get(kase, 'case.service.name'), [kase]);

  const notes = useMemo(() => getDisplayNotes({
    notesData,
    isPopulateLoading,
    serviceName,
    subjectName,
  }), [notesData, serviceName, subjectName, isPopulateLoading]);

  return {
    notes,
    isLoading: isLoading || isPopulateLoading,
    response: notesResponse,
    page,
    pageSize,
    setPageSize,
    setPageNumber: setPage,
  };
};

export const useOutcomeNotes = (item) => {
  let caseId;
  let providerId;
  if (item.case) {
    caseId = get(item, 'case.id', '');
    providerId = get(item, 'referred_to_group.id', '');
  } else {
    caseId = get(item, 'id', '');
    providerId = get(item, 'provider.id', '');
  }

  const { data: notesResponse } = useFind(
    'notes',
    {
      'subject.type': 'case',
      subject: caseId,
      'details.context': 'closed',
      category: 'general',
    },
    {
      queryConfig: {
        enabled: Boolean(caseId && providerId),
      },
    },
  );
  const notes = notesResponse.data.data;
  usePopulate('employee', 'employee', notes);
  usePopulate('employee.provider', 'provider', notes);
  return notes;
};

export const useReasonNote = (referral, providerId) => {
  const referralId = get(referral, 'id');
  const referralState = get(referral, 'state');
  const { data: notesResponse } = useFind('notes', {
    'subject.type': 'referral',
    subject: `${referralId}`,
    'details.context': referralState === 'in_review' ? 'held_in_review' : `${referralState}`,
    category: 'general',
  }, {
    queryConfig: {
      enabled: Boolean(referralId && providerId),
    },
  });
  const reasonNote = notesResponse.data.data[0];

  return reasonNote;
};
